/**
 * Silent Axon SSO token refresh (frontend-coordinated refresh + backend refresh endpoint).
 *
 * When a request returns 401 the access token has expired or been revoked. Rather than bouncing
 * the user through the full SSO login flow, the client asks the backend to rotate the token pair
 * using the stored (httponly) refresh cookie, then retries the original request.
 *
 * Refresh token rotation is enabled on the IDP, so concurrent refreshes are treated as replay and
 * end the session. All refreshes are therefore serialized across tabs with the Web Locks API, and
 * deduplicated within a burst via a shared "last refresh" timestamp.
 */

import { getIsLoggingOut } from "api/utils";
import { JANZU_URL } from "config/config";
import { getCookie } from "utils/cookies";

import {
  getLastRefreshAt,
  setLastRefreshAt,
  shouldRefreshAccessToken,
} from "./storage";

const REFRESH_LOCK = "gl-session-refresh";
const REFRESH_URL = `${JANZU_URL}authz/refresh`;
const REFRESH_MAX_RETRIES = 3;
const REFRESH_RETRY_DELAY_MS = 200;
const RECENT_REFRESH_DEDUP_MS = 5 * 1000;
// TODO: Remove this temporary Axon SSO investigation logging after silent refresh is stable.
const REFRESH_DEBUG_STORAGE_KEY = "gl-session-refresh-debug";

/**
 * Returns whether verbose session refresh diagnostics should be printed to the browser console.
 */
const isRefreshDebugEnabled = (): boolean => {
  try {
    return localStorage.getItem(REFRESH_DEBUG_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
};

/**
 * Prints opt-in session refresh diagnostics without exposing token values.
 */
const logRefreshDebug = (message: string, context?: Record<string, unknown>): void => {
  if (!isRefreshDebugEnabled()) {
    return;
  }
  console.debug(`[session-refresh] ${message}`, context || {});
};

/**
 * Calls the backend refresh endpoint, retrying on transient failures.
 * Returns true if the session was successfully rotated.
 * Fails immediately on 401 (definitive auth failure) without retrying.
 */
const postRefresh = async (attempt = 0): Promise<boolean> => {
  if (attempt > 0) {
    logRefreshDebug("retrying refresh POST after backoff", { attempt });
    await new Promise<void>(resolve => {
      setTimeout(resolve, REFRESH_RETRY_DELAY_MS * attempt);
    });
  }
  try {
    logRefreshDebug("starting refresh POST", { attempt });
    const response = await fetch(REFRESH_URL, {
      method: "POST",
      headers: { "X-CSRFToken": getCookie("csrftoken") || "" },
    });
    if (response.ok) {
      setLastRefreshAt(Date.now());
      logRefreshDebug("refresh POST succeeded", { attempt, status: response.status });
      return true;
    }
    if (response.status === 401) {
      logRefreshDebug("refresh POST returned definitive auth failure", { attempt, status: response.status });
      return false; // Definitive auth failure; no point retrying.
    }
    logRefreshDebug("refresh POST returned transient failure", { attempt, status: response.status });
    // Non-401, non-ok (e.g. 5xx): transient failure, fall through to retry.
  } catch (error) {
    logRefreshDebug("refresh POST threw network error", {
      attempt,
      error: error instanceof Error ? error.message : String(error),
    });
    // Network error: fall through to retry.
  }
  if (attempt >= REFRESH_MAX_RETRIES) {
    logRefreshDebug("refresh retries exhausted", { attempt });
    return false;
  }
  return postRefresh(attempt + 1);
};

/**
 * Returns whether an existing successful refresh is recent enough to satisfy this caller.
 */
const shouldSkipRefresh = (requestStartedAt: number): boolean => {
  const lastRefreshAt = getLastRefreshAt();
  return lastRefreshAt > requestStartedAt || Date.now() - lastRefreshAt < RECENT_REFRESH_DEDUP_MS;
};

/**
 * Attempt to refresh the session, serializing across tabs so only one refresh is ever in flight.
 * Skips the network call if another refresh already completed after the calling request started
 * or if a provider-level preemptive refresh just completed before a later 401 handler ran.
 */
export const attemptSessionRefresh = async (requestStartedAt: number): Promise<boolean> => {
  logRefreshDebug("attempting session refresh", {
    hasWebLocks: Boolean(navigator.locks),
    lastRefreshAt: getLastRefreshAt(),
    requestStartedAt,
  });
  // navigator.locks is unavailable in insecure contexts and older browsers; fall back to the
  // localStorage dedup check which at least prevents obvious same-tab duplication. True
  // cross-tab serialization is not possible without Web Locks, but the dedup check eliminates
  // the common case of a burst of parallel 401s all racing to refresh.
  if (!navigator.locks) {
    if (shouldSkipRefresh(requestStartedAt)) {
      logRefreshDebug("skipping refresh because another request already refreshed without Web Locks");
      return true;
    }
    logRefreshDebug("refreshing without Web Locks");
    return postRefresh();
  }
  return navigator.locks.request(REFRESH_LOCK, async () => {
    if (shouldSkipRefresh(requestStartedAt)) {
      logRefreshDebug("skipping refresh because another request already refreshed while waiting for lock");
      return true; // another refresh already produced a fresh token while we waited for the lock
    }
    logRefreshDebug("refreshing inside Web Lock");
    return postRefresh();
  });
};

/**
 * Fetch wrapper that transparently refreshes the session on a 401 and retries the request once.
 * Auth happens in middleware before the view runs, so a 401 means the request had no side effects
 * and is safe to retry (including mutations). Falls back to returning the original 401 response so
 * existing handlers can redirect to login when the session truly cannot be refreshed.
 *
 * Note: requests with a ReadableStream body cannot be retried because the stream is consumed by
 * the first fetch. FormData, JSON strings, and other non-stream bodies are safe to retry.
 */
export const fetchWithRefresh = async (input: RequestInfo, init?: RequestInit): Promise<Response> => {
  const requestStartedAt = Date.now();
  if (!getIsLoggingOut() && shouldRefreshAccessToken(requestStartedAt)) {
    logRefreshDebug("access token expired before request; attempting preemptive refresh");
    await attemptSessionRefresh(requestStartedAt);
  }

  const response = await fetch(input, init);
  if (response.status !== 401 || getIsLoggingOut()) {
    return response;
  }
  // ReadableStream bodies are consumed by the first fetch and cannot be retried. Guard the global
  // with typeof since it is not defined in every environment (e.g. the jsdom test runtime).
  if (typeof ReadableStream !== "undefined" && init?.body instanceof ReadableStream) {
    logRefreshDebug("not refreshing request with non-retryable stream body");
    return response;
  }
  const refreshed = await attemptSessionRefresh(requestStartedAt);
  if (!refreshed) {
    logRefreshDebug("session refresh failed; returning original 401");
    return response;
  }
  logRefreshDebug("session refresh succeeded; retrying original request");
  return fetch(input, init);
};
